<script setup lang="ts">
import { computed } from "vue";
import { severityLabel, type Severity } from "../api";

/**
 * Single source of truth for severity *rendering* (colour) across the app. The
 * label text is shared with the rest of the app via `severityLabel` in api.ts.
 *
 * Colour + label semantics (intentional, per UX direction):
 *
 *   none → green; rendered as the word "Pass" — "no quality events" is the
 *                  positive outcome, so it gets a positive label.
 *   low       → amber   — every quality event is a concern, even at the low end
 *   medium    → orange  — escalating warning
 *   high      → red-200 — strong warning
 *   critical  → red     — danger
 *
 * Blue (info-style) is intentionally NOT used for severity, since blue reads as
 * "good/informational" in our palette and quality severities are never "good".
 *
 * When `finalSeverity` differs from `severity`, the badge renders the final
 * value with the original struck through, e.g. "Severity: high ~~critical~~".
 */
const props = withDefaults(
  defineProps<{
    severity: Severity;
    finalSeverity?: Severity | null;
    prefix?: string;
  }>(),
  { finalSeverity: null, prefix: "" },
);

const displayValue = computed<Severity>(
  () => props.finalSeverity ?? props.severity,
);
const strikethrough = computed<Severity | null>(() => {
  if (props.finalSeverity && props.finalSeverity !== props.severity) {
    return props.severity;
  }
  return null;
});
</script>

<template>
  <span class="sev-badge" :class="`sev-${displayValue}`">
    <template v-if="prefix">{{ prefix }}: </template>
    <span class="sev-value">{{ severityLabel(displayValue) }}</span>
    <s v-if="strikethrough" class="sev-strike">{{ severityLabel(strikethrough) }}</s>
  </span>
</template>

<style scoped>
.sev-badge {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  line-height: 1.4;
  white-space: nowrap;
}
.sev-value { font-weight: 700; }
.sev-strike {
  font-weight: 500;
  opacity: 0.65;
}

.sev-none {
  background: #d1fae5;
  color: #065f46;
}
.sev-low {
  background: #fef3c7;
  color: #78350f;
}
.sev-medium {
  background: #fed7aa;
  color: #7c2d12;
}
.sev-high {
  background: #fecaca;
  color: #7f1d1d;
}
.sev-critical {
  background: #b91c1c;
  color: #fff7ed;
}
</style>
